<template>
  <v-card>
    <v-card-title>
      <div class="card-title">
        <span class="bold table-card-title">
          <slot name="title">Minhas campanhas</slot>
        </span>

        <div class="status-search-group">
          <mf-status-filter v-if="itemsFilter.length > 1" :items="itemsFilter" @input="getFilterResult" />
          <div class="min-width-345">
            <mf-search v-model="search" class="pt-0 mt-0" hide-details placeholder="Busca" />
          </div>
        </div>
      </div>
    </v-card-title>

    <v-card-text class="pa-0">
      <paginated-data-table
        ref="table"
        :class="industry ? 'industry-striped' : ''"
        :headers="headers"
        :search="search"
        :query="query"
        :custom-items-per-page="franchisee ? 5 : 10"
        query-name="campaigns"
        :query-variables="queryVariables"
        :graphql-uri="$microservicesUrls['campaigns']"
      >
        <template v-slot:item="{ item }">
          <tr class="colorful-border" :style="{ borderColor: getStatusLabel(item.status).color }">
            <td>
              <mf-icon fill small :color="getStatusLabel(item.status).color">fiber_manual_record</mf-icon>
              {{ getStatusLabel(item.status).text }}
            </td>
            <td v-if="industry" class="nowrap-row" style="max-width: 300px">
              <tooltip :message="item.title">
                <span v-if="franchisee || item.status === 'EXPIRED'">{{ item.title | truncate({ length: titleTruncate }) }}</span>
                <span v-else>
                  <router-link class="link" :to="{ path: link(item._id, item.status) }">
                    {{ item.title | truncate({ length: titleTruncate }) }}
                  </router-link>
                </span>
              </tooltip>
            </td>
            <td v-else class="nowrap-row" style="max-width: 300px">
              <mf-tooltip max-width="250" top>
                <template #activator="{ on }">
                  <span v-if="!franchisee" v-on="on"
                    ><router-link class="link" :to="{ path: link(item._id, item.status) }">
                      {{ item.title | truncate({ length: titleTruncate }) }}
                    </router-link></span
                  >
                  <span v-else v-on="on">{{ item.title | truncate({ length: titleTruncate }) }}</span>
                </template>
                <span>{{ item.title }}</span>
              </mf-tooltip>
            </td>
            <td v-if="!industry" class="nowrap-row" style="max-width: 300px">
              <mf-tooltip max-width="250" top>
                <template #activator="{ on }">
                  <span v-on="on">{{ item.description | truncate({ length: 150 }) }}</span>
                </template>
                <span>{{ item.description | hifen }}</span>
              </mf-tooltip>
            </td>
            <td v-if="mode === 'whatsapp'">
              {{ getWhatsappTargetingModeDescription(item.targeting_mode) }}
            </td>
            <td v-if="mode === 'whatsapp'">
              <mf-tooltip max-width="250" top>
                <template #activator="{ on }">
                  <span v-on="on">{{ getSafe(() => item.senders.length) | hifen }}</span>
                </template>
                <span v-for="sender in getSafe(() => item.senders)" :key="sender.number" style="display: block">
                  {{ phoneNumberFormat(sender.number) }} - {{ sender.name }}
                </span>
              </mf-tooltip>
            </td>
            <td>{{ getMessageDates(item.message.send_date, item.message.send_time) }}</td>
            <td>
              {{
                item.initial_date && item.final_date
                  ? `${DateTime.fromISO(item.initial_date).toFormat('dd/MM/yyyy')} - ${DateTime.fromISO(item.final_date).toFormat('dd/MM/yyyy')}`
                  : '-'
              }}
            </td>
            <td>{{ DateTime.fromISO(item.updated_at).toFormat('dd/MM/yyyy HH:mm:ss') }}</td>
            <td v-if="industry">{{ item.industry.name }}</td>
            <td class="text-right">
              <v-menu v-if="!franchisee" :close-on-content-click="false">
                <template v-slot:activator="{ on }">
                  <mf-button type="icon" icon="more_vert" color="cinza-0" v-on:click.native="on.click" />
                </template>
                <v-list>
                  <v-list-item :disabled="item.status === 'EXPIRED'" @click="$goTo(link(item._id, item.status))">
                    <v-list-item-title>{{ hasResults(item.status) ? 'Resultados' : industry ? 'Visualizar' : 'Editar' }}</v-list-item-title>
                  </v-list-item>

                  <tooltip align="left" :message="tooltipMessage" :disabled="!industry">
                    <v-list-item :disabled="industry" @click="cloneCampaign(item._id, item.name ? item.name : item.title)">
                      <v-list-item-title>Duplicar</v-list-item-title>
                    </v-list-item>
                  </tooltip>

                  <tooltip align="left" :message="tooltipMessage" :disabled="!industry">
                    <v-list-item
                      v-if="item.status === 'DRAFT' || item.status === 'INACTIVE'"
                      :disabled="industry"
                      @click="deleteCampaign(item._id, item.title)"
                    >
                      <v-list-item-title color="error" :loading="$wait.is('remove-campaign')">{{ $t('campanhas.deletar_campanha') }}</v-list-item-title>
                    </v-list-item>
                  </tooltip>

                  <tooltip align="left" :message="tooltipMessage" :disabled="!industry">
                    <v-list-item v-if="item.status === 'SCHEDULED'" :disabled="industry" @click="unscheduleCampaign(item._id)">
                      <v-list-item-title> Desativar </v-list-item-title>
                    </v-list-item>
                  </tooltip>

                  <tooltip align="left" :message="tooltipMessage" :disabled="!industry">
                    <v-list-item v-if="checkIfCampaignIsCancelable(item)" :disabled="industry" @click="cancelRecurrentCampaign(item.mode, item._id)">
                      <v-list-item-title> Cancelar </v-list-item-title>
                    </v-list-item>
                  </tooltip>

                  <mf-tooltip
                    v-if="checkIfCampaignSendingIsCancelable(item) && campaignCancelSendingsDisabledTooltip(item)"
                    max-width="250"
                    left
                    variant="primary"
                  >
                    <template #activator>
                      <v-list-item disabled>
                        <v-list-item-title> Cancelar disparos </v-list-item-title>
                      </v-list-item>
                    </template>
                    {{ campaignCancelSendingsDisabledTooltip(item) }}
                  </mf-tooltip>
                  <v-list-item v-else-if="checkIfCampaignSendingIsCancelable(item)" @click="cancelSendings(item.mode, item._id)">
                    <v-list-item-title> Cancelar disparos </v-list-item-title>
                  </v-list-item>

                  <v-list-item @click="openLogsModal(item._id)">
                    <v-list-item-title> Logs </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </td>
          </tr>
        </template>
      </paginated-data-table>
      <logs-modal v-model="logsDialog" :tags="logsTag" :log-title="mode" :id-item="campaignsId" />
    </v-card-text>
  </v-card>
</template>

<script>
import LogsModal from '@/components/logs-modal/LogsModal.vue'
import PaginatedDataTable from '@/components/PaginatedDataTable.vue'
import {
  MUTATION_CLONE_CAMPAIGN,
  QUERY_GET_CAMPAIGNS,
  MUTATION_DELETE_CAMPAIGN,
  MUTATION_UNSCHEDULE_CAMPAIGN,
  MUTATION_CANCEL_WHATSAPP_SENDINGS,
  MUTATION_CANCEL_SMS_CAMPAIGN,
  MUTATION_CANCEL_EMAIL_CAMPAIGN,
  MUTATION_CANCEL_PUSH_CAMPAIGN,
} from '@/modules/campanhas/views/components/graphql'
import { DateTime } from 'luxon'
import Tooltip from '@/modules/campanhas/gestao-ofertas-v2/views/components/Tooltip'
import { getStatusLabel } from '@/config/helpers.js'
import Swal from 'sweetalert2'
import { phoneNumberFormat } from '@/helpers/phoneNumberFormat.js'
import { mapGetters } from 'vuex'

export default {
  components: {
    PaginatedDataTable,
    LogsModal,
    Tooltip,
  },
  filters: {
    hifen(value) {
      if (!value) return '-'
      return value
    },
  },
  props: {
    mode: {
      type: String,
      required: true,
    },
    franchisee: {
      type: Boolean,
      default: false,
    },
    logsTag: {
      type: String,
      default: '',
    },
    industry: {
      type: Boolean,
      default: false,
    },
    itemsFilter: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    search: '',
    campaignsId: '',
    logsDialog: false,
    DateTime: DateTime,
    tooltipMessage: 'Essa campanha faz parte de uma oferta de trade e não pode ser editada',
    statusToSearch: [],
  }),

  computed: {
    ...mapGetters(['getSelectedIndustriesIds']),

    parsedStatus() {
      return this.statusToSearch ? this.statusToSearch : []
    },
    query() {
      return QUERY_GET_CAMPAIGNS
    },
    queryVariables() {
      let queryVariables = {
        mode: [this.mode.toUpperCase()],
        franchise: this.franchisee,
        isIndustry: this.industry,
        status: this.parsedStatus,
      }

      if (this.industry) {
        queryVariables.industryIds = this.getSelectedIndustriesIds
      }
      return queryVariables
    },
    headers() {
      let headers = [
        {
          text: 'Status',
          value: 'status',
        },
        {
          text: 'Campanha',
          value: 'title',
        },
        {
          text: 'Descrição',
          value: 'description',
        },
        ...(this.mode === 'whatsapp'
          ? [
              {
                text: 'Audiência',
                value: 'targeting_mode',
                sortable: false,
              },
              {
                text: 'Disparadores',
                value: 'senders',
                sortable: false,
              },
            ]
          : []),
        {
          text: 'Data de envio',
          value: 'send_datetime',
        },
        {
          text: 'Data de validade',
          value: 'final_date',
        },
        {
          text: 'Última atualização',
          value: 'updated_at',
        },
        {
          text: 'Indústria',
          value: 'industry.name',
          isActive: true,
          isDefault: true,
          isRequired: false,
        },
        {
          text: '',
          sortable: false,
        },
      ]

      if (this.industry) {
        headers = headers.filter((h) => h.text !== 'Descrição')
      } else {
        headers = headers.filter((h) => h.text !== 'Indústria')
      }

      return headers
    },
    titleTruncate() {
      return this.industry ? 140 : 70
    },
  },
  beforeMount() {
    this.statusToSearch = this.itemsFilter.map((item) => item.value)
  },
  mounted() {
    this.$nextTick(() => {
      this.$bus.$on('filter', () => {
        this.$refs.table.waitAndGetData()
      })
    })
  },
  beforeDestroy() {
    this.$bus.$off('filter')
  },
  methods: {
    phoneNumberFormat,
    getStatusLabel,
    getWhatsappTargetingModeDescription(value) {
      switch (value) {
        case 'ALL':
          return 'Total'
        case 'LOYALTY':
          return 'Fidelidade'
        case 'NON_LOYALTY':
          return 'Não Fidelidade'
        case 'SEGMENTED':
          return 'Segmentada'
      }
      return ''
    },
    getFilterResult(value) {
      this.statusToSearch = value.length ? value : this.itemsFilter.map((item) => item.value)
    },
    openLogsModal(id) {
      this.campaignsId = id
      this.logsDialog = true
    },
    getMessageDates(date, time = null) {
      let messageData = ''
      let dataFormater = ''
      let formatData = ''
      if (time) {
        messageData = ''.concat(date, ' ', time)
        dataFormater = 'dd/MM/yyyy | HH:mm'
        formatData = DateTime.fromFormat(messageData, 'yyyy-MM-dd HH:mm').toFormat(dataFormater)
      } else {
        messageData = date
        dataFormater = 'dd/MM/yyyy'
        formatData = DateTime.fromISO(messageData).toFormat(dataFormater)
      }
      return date ? formatData : '-'
    },
    async cloneCampaign(id, name) {
      const confirmed = await this.$confirm({
        ...this.$t('campanhas.confirmations.clone'),
        message: `Deseja duplicar ${name ? 'a campanha ' + name.trim().bold() : 'esta campanha'}?`,
      })
        .then(() => true)
        .catch(() => false)
      if (!confirmed) return
      this.$refs.table.paginationLoading = true
      await this.$apollo
        .mutate({
          mutation: MUTATION_CLONE_CAMPAIGN,
          variables: {
            id,
          },
          context: {
            uri: this.$microservicesUrls['campaigns'],
          },
        })
        .then(
          ({
            data: {
              cloneCampaign: { _id: newId },
            },
          }) => {
            // Temporary fix for whatsapp - FIX THIS
            const mode = this.mode === 'push' ? 'aplicativo' : this.mode
            if (mode === 'whatsapp') {
              this.$router.push(`/campanhas/${mode}/editar/${newId}`).catch(() => {})
            } else {
              this.$router.push(`/campanhas/${mode}/${newId}`).catch(() => {})
            }
          }
        )
        .catch(() => {
          this.$snackbar(this.$t('campanhas.snackbars.errors.clone'))
        })
    },
    async deleteCampaign(id, name) {
      const confirmed = await this.$confirm({
        title: 'Remover campanha',
        message: `Tem certeza que deseja remover a campanha "${name}"?`,
        confirmText: 'Remover',
        confirmColor: 'vermelho-d20',
        cancelText: 'Cancelar',
      })
        .then(() => true)
        .catch(() => false)
      if (!confirmed) return
      this.$refs.table.paginationLoading = true
      await this.$apollo
        .mutate({
          mutation: MUTATION_DELETE_CAMPAIGN,
          variables: {
            id,
          },
          context: {
            uri: this.$microservicesUrls['campaigns'],
          },
        })
        .then(() => {
          this.$refs.table.waitAndGetData()
        })
        .catch(() => {
          this.$snackbar(this.$t('campanhas.snackbars.errors.delete'))
        })
      this.$refs.table.paginationLoading = false
    },
    async unscheduleCampaign(id) {
      const confirmed = await this.$confirm(this.$t('campanhas.confirmations.unschedule'))
        .then(() => true)
        .catch(() => false)
      if (!confirmed) return
      this.$refs.table.paginationLoading = true
      await this.$apollo
        .mutate({
          mutation: MUTATION_UNSCHEDULE_CAMPAIGN,
          variables: {
            id,
          },
          context: {
            uri: this.$microservicesUrls['campaigns'],
          },
        })
        .then(() => {
          this.$store.commit('smsCountsChanged', true)
          this.$refs.table.waitAndGetData()
        })
        .catch(() => {
          this.$snackbar(this.$t('campanhas.snackbars.errors.unschedule'))
        })
      this.$refs.table.paginationLoading = false
    },
    hasResults(status) {
      return ['ACTIVE', 'FINISHED'].includes(status)
    },
    link(param, status) {
      const mode = this.mode === 'push' ? 'aplicativo' : this.mode
      if (this.industry) {
        if (status === 'INACTIVE' || status === 'DRAFT' || status === 'SCHEDULED') return `/industria/sms-trade/editar/${param}`
        else if (status === 'FINISHED' || status === 'ACTIVE') return `/industria/sms-trade/resultados/${param}`
      } else {
        if (status === 'DRAFT' || status === 'SCHEDULED') return `/campanhas/${mode.toLowerCase()}/editar/${param}`
        else if (status === 'FINISHED' || status === 'ACTIVE') return `/campanhas/${mode.toLowerCase()}/resultados/${param}`
      }
    },
    checkIfCampaignIsCancelable(item) {
      const campaignIsRecurrent = item?.frequency?.type === 'RECURRENT'

      return ['SMS', 'EMAIL', 'PUSH'].includes(item.mode) && item.status === 'ACTIVE' && campaignIsRecurrent
    },
    checkIfCampaignSendingIsCancelable(item) {
      if (this.$getAllowedModules()['external_whatsapp_provider']) return false
      if (item.mode !== 'WHATSAPP') return false
      if (item.status !== 'ACTIVE') return false
      return true
    },
    campaignCancelSendingsDisabledTooltip(item) {
      const now = DateTime.now().toFormat('yyyy-MM-dd')
      if (item.message?.send_date !== now) return this.$t('campanhas.tooltips.cancel_sendings.out_of_date')
      if (item.message?.cancelation?.canceled) return this.$t('campanhas.tooltips.cancel_sendings.already_canceled')
      return ''
    },
    async cancelSendings() {
      this.$confirm(this.$t('campanhas.confirmations.cancel_sendings'))
        .then(async () => {
          try {
            await this.$apollo.mutate({
              mutation: MUTATION_CANCEL_WHATSAPP_SENDINGS,
              variables: {
                id: this.campaign._id,
              },
              context: {
                uri: this.$microservicesUrls['campaigns'],
              },
            })
            Swal.fire({
              text: 'Disparos cancelados',
              icon: 'success',
              showConfirmButton: false,
              timer: 1000,
              customClass: {
                content: 'content-class',
              },
            })
            this.$refs.table.waitAndGetData()
          } catch (error) {
            console.log(error)
            this.$snackbar({ message: 'Erro ao cancelar disparos.', snackbarColor: 'error' })
          }
        })
        .catch(() => {})
    },
    cancelRecurrentCampaign(campaignType, id) {
      const mutations = {
        SMS: MUTATION_CANCEL_SMS_CAMPAIGN,
        EMAIL: MUTATION_CANCEL_EMAIL_CAMPAIGN,
        PUSH: MUTATION_CANCEL_PUSH_CAMPAIGN,
      }

      this.$confirm(this.$t('campanhas.confirmations.cancel'))
        .then(async () => {
          try {
            await this.$apollo.mutate({
              mutation: mutations[campaignType],
              variables: { id },
              context: { uri: this.$microservicesUrls['campaigns'] },
            })
            if (campaignType === 'SMS') this.$store.commit('smsCountsChanged', true)
            this.$refs.table.waitAndGetData()
          } catch (error) {
            console.log(error)
            this.$snackbar(this.$t('campanhas.snackbars.errors.cancel'))
          }
        })
        .catch(() => {})
    },
  },
}
</script>

<style lang="scss">
.card-title {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding-left: 0;
  padding-bottom: 16px;
}

.status-search-group {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
}

.industry-striped {
  tr:nth-child(even) {
    background-color: #f7f7f7;
  }
}

.text-align-right {
  text-align: right;
  margin-right: 12px;
}

.colorful-border {
  border-left: 3px solid;
}
</style>
